// ============================================================
// EmailChangeDialog.tsx
// Small Casevo dialog for entering a NEW login email. Used by:
//   • Settings → Profile "Request email change" (user → notifies admins)
//   • Admin → Team member modal "Change email" (admin actions it)
// The caller supplies onSubmit (throws with a user-facing message on
// failure) and the success copy; the dialog owns input, format validation,
// busy + error state, and the success step. Built on DialogueBox (surface
// card, token colours → light + dark).
// ============================================================
import React, { useEffect, useState } from 'react'
import DialogueBox from './DialogueBox'
import { sInput, S_LABEL, S_NOTE, S_FIELD_ERROR, S_BTN_GHOST, S_BTN_PRIMARY, S_FORMFOOT } from '../settings/settingsUi'

// Mirrors the backend's normaliseEmail (emailChangeController.ts).
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
export const isValidEmail = (v: string): boolean => {
  const e = v.trim()
  return e.length > 0 && e.length <= 254 && EMAIL_RE.test(e)
}

interface EmailChangeDialogProps {
  isOpen: boolean
  title: string
  intro: React.ReactNode
  currentEmail: string
  submitLabel: string
  busyLabel: string
  // Resolve = success; throw Error(message) = show the message inline.
  onSubmit: (newEmail: string) => Promise<void>
  // Success step body. Rendered after onSubmit resolves.
  successContent: (newEmail: string) => React.ReactNode
  successButtonLabel?: string
  onClose: () => void
  // Fired when the success step's button is pressed (defaults to onClose).
  onDone?: () => void
}

const EmailChangeDialog: React.FC<EmailChangeDialogProps> = ({
  isOpen, title, intro, currentEmail, submitLabel, busyLabel, onSubmit,
  successContent, successButtonLabel = 'Done', onClose, onDone,
}) => {
  const [value, setValue] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [doneEmail, setDoneEmail] = useState<string | null>(null)

  // Fresh form each time it opens.
  useEffect(() => {
    if (!isOpen) return
    setValue('')
    setError(null)
    setBusy(false)
    setDoneEmail(null)
  }, [isOpen])

  // Esc closes (not while a request is in flight).
  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape' || busy) return
      e.stopImmediatePropagation()
      if (doneEmail) (onDone ?? onClose)()
      else onClose()
    }
    window.addEventListener('keydown', onKey, { capture: true })
    return () => window.removeEventListener('keydown', onKey, { capture: true })
  }, [isOpen, busy, doneEmail, onClose, onDone])

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    const next = value.trim().toLowerCase()
    if (!isValidEmail(next)) {
      setError('Please enter a valid email address.')
      return
    }
    if (next === currentEmail.trim().toLowerCase()) {
      setError('That is the current email address.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await onSubmit(next)
      setDoneEmail(next)
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : 'Something went wrong. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <DialogueBox isOpen={isOpen} title={title} onClose={() => { if (!busy) (doneEmail ? (onDone ?? onClose) : onClose)() }}>
      {doneEmail ? (
        <div>
          <div className="text-[14px] text-casevo-forest leading-[1.5]" role="status">{successContent(doneEmail)}</div>
          <div className={`${S_FORMFOOT} justify-end`}>
            <button type="button" onClick={() => (onDone ?? onClose)()} className={S_BTN_PRIMARY} autoFocus>
              {successButtonLabel}
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={(e) => { void submit(e) }} noValidate>
          <div className={`${S_NOTE} mb-[14px]`}>{intro}</div>
          <p className={S_LABEL}>Current email</p>
          <p className="text-[14px] text-casevo-forest font-medium break-all mb-[14px]">{currentEmail}</p>
          <label htmlFor="email-change-new" className={S_LABEL}>New email</label>
          <input
            id="email-change-new"
            type="email"
            inputMode="email"
            autoComplete="off"
            autoFocus
            value={value}
            onChange={(e) => { setValue(e.target.value); if (error) setError(null) }}
            disabled={busy}
            aria-invalid={!!error}
            aria-describedby={error ? 'email-change-error' : undefined}
            placeholder="name@example.com"
            className={sInput(!!error)}
          />
          {error && <p id="email-change-error" className={S_FIELD_ERROR} role="alert">{error}</p>}
          <div className={`${S_FORMFOOT} justify-end`}>
            <button type="button" onClick={onClose} disabled={busy} className={S_BTN_GHOST}>Cancel</button>
            <button type="submit" disabled={busy || value.trim().length === 0} className={S_BTN_PRIMARY}>
              {busy ? busyLabel : submitLabel}
            </button>
          </div>
        </form>
      )}
    </DialogueBox>
  )
}

export default EmailChangeDialog
